import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import DiamondIcon from "@mui/icons-material/Diamond";
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../../components/navbar/navbar.component";
import { schemesV2Api } from "../../services/schemesV2Api";
import { generalToastStyle } from "../../utils/toast.styles";

const SchemesV2MySchemes = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [enrollments, setEnrollments] = useState([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserSchemes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const userId = localStorage.getItem('user_id');
        if (!userId) {
          toast.error("Please login to view your schemes", generalToastStyle);
          navigate('/signin');
          return;
        }

        const response = await schemesV2Api.getUserSchemes(userId);
        console.log('API Response:', response);
        
        // Handle the actual API response structure
        const activeSchemes = response.data?.activeSchemes || [];
        const completedSchemes = response.data?.completedSchemes || [];
        const allSchemes = [...activeSchemes, ...completedSchemes];
        
        setEnrollments(allSchemes);
        
        // Apply scheme filter if provided in URL
        const schemeFilter = searchParams.get('scheme');
        let filteredSchemes = allSchemes;
        
        if (schemeFilter) {
          filteredSchemes = allSchemes.filter(enrollment => 
            enrollment.scheme_id === schemeFilter || 
            enrollment.scheme_name?.toLowerCase().includes(schemeFilter.toLowerCase())
          );
        }
        
        setFilteredEnrollments(filteredSchemes);
        setSummary({
          totalInvestment: response.data?.totalInvestment || 0,
          totalBonusEarned: response.data?.totalBonusEarned || 0,
          totalGoldAccumulated: response.data?.totalGoldAccumulated || 0,
          totalEnrollments: allSchemes.length,
          activeEnrollments: activeSchemes.length,
          completedEnrollments: completedSchemes.length,
          totalInvested: response.data?.totalInvestment || 0
        });
      } catch (err) {
        setError(err.message);
        toast.error(`Failed to load your schemes: ${err.message}`, generalToastStyle);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserSchemes();
  }, [navigate, searchParams]);


  const getStatusChip = (status) => {
    const colors = {
      active: { bg: "#e8f5e8", color: "#2e7d32" },
      completed: { bg: "#e3f2fd", color: "#1976d2" },
      paused: { bg: "#fff3e0", color: "#f57c00" },
      cancelled: { bg: "#ffebee", color: "#d32f2f" }
    };
    
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        sx={{
          backgroundColor: colors[status]?.bg || colors.active.bg,
          color: colors[status]?.color || colors.active.color,
          fontWeight: 600,
          fontSize: "12px"
        }}
      />
    );
  };

  const handleViewDetails = (enrollmentId) => {
    navigate(`/schemes-v2/payment/${enrollmentId}`);
  };

  const handleEnrollNew = () => {
    navigate('/schemes-v2/selection');
  };

  const handleMakePayment = (enrollmentId) => {
    navigate(`/schemes-v2/payment/${enrollmentId}`);
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ color: "#A36E29", mb: 2 }} 
          />
          <Typography variant="h6" sx={{ color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
            Loading your schemes...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Navbar />
        <Box
          sx={{
            background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
            minHeight: "100vh",
            pt: { xs: 15, md: 12 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <Typography variant="h6" sx={{ color: "#d32f2f", mb: 2 }}>
            Error loading your schemes
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ backgroundColor: "#A36E29" }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <Navbar />
      
      <Box
        sx={{
          background: "linear-gradient(to bottom, rgb(249, 236, 220), rgb(231, 192, 125))",
          minHeight: "100vh",
          pt: { xs: 15, md: 12 },
          pb: 4
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                color: "#A36E29",
                fontWeight: 700,
                mb: 1,
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              My Schemes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(163, 110, 41, 0.8)",
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Track your savings progress and manage your enrollments
            </Typography>
          </Box>

          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, textAlign: "center" }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                      {summary?.totalEnrollments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Total Schemes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, textAlign: "center" }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#4caf50", fontFamily: '"Roboto", sans-serif' }}>
                      {summary?.activeEnrollments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Active Schemes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, textAlign: "center" }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#2196f3", fontFamily: '"Roboto", sans-serif' }}>
                      {summary?.completedEnrollments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3, textAlign: "center" }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                      ₹{summary.totalInvested?.toLocaleString() || "0"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                      Total Invested
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Enrollments List */}
          {(!filteredEnrollments || filteredEnrollments.length === 0) ? (
            <Card sx={{ borderRadius: 3, textAlign: "center" }}>
              <CardContent sx={{ py: 6 }}>
                <DiamondIcon sx={{ fontSize: 80, color: "#A36E29", mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontFamily: '"Roboto", sans-serif' }}>
                  {searchParams.get('scheme') ? 'No Schemes Found' : 'No Active Schemes'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: '"Roboto", sans-serif' }}>
                  {searchParams.get('scheme') 
                    ? `No schemes found matching "${searchParams.get('scheme')}". Try browsing all schemes.`
                    : 'Start your savings journey by enrolling in one of our schemes'
                  }
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleEnrollNew}
                  sx={{
                    background: "linear-gradient(to right, #d4a76a, #A36E29)",
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    fontFamily: '"Roboto", sans-serif',
                    "&:hover": {
                      background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                    }
                  }}
                >
                  Browse Schemes
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {(filteredEnrollments || []).map((enrollment) => (
                <Grid item xs={12} md={6} key={enrollment.id}>
                  <Card sx={{ 
                    borderRadius: 3, 
                    overflow: "hidden", 
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}>
                    <CardContent sx={{ 
                      p: 3, 
                      height: "100%", 
                      display: "flex", 
                      flexDirection: "column",
                      flex: 1
                    }}>
                      {/* Header */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            {enrollment.scheme_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            {enrollment.scheme_type === 'daily' ? 'Daily' : 'Monthly'} Scheme
                          </Typography>
                        </Box>
                        {getStatusChip(enrollment.status)}
                      </Box>

                      {/* Progress */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                            Progress
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                            {enrollment.plan_progress?.percentage || 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={enrollment.plan_progress?.percentage || 0}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "#f5f5f5",
                            "& .MuiLinearProgress-bar": {
                              background: "linear-gradient(to right, #d4a76a, #A36E29)"
                            }
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: '"Roboto", sans-serif' }}>
                          {enrollment.plan_progress?.periods_paid || 0} of {enrollment.plan_progress?.total_periods || 0} installments paid
                        </Typography>
                      </Box>

                      {/* Details */}
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              Installment
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              ₹{parseFloat(enrollment.instalment_amount || 0).toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              Total Invested
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              ₹{parseFloat(enrollment.plan_progress?.total_paid || 0).toLocaleString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              Gold Grams
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              {parseFloat(enrollment.gold_grams_accumulated || 0).toFixed(4)}g
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Roboto", sans-serif' }}>
                              Start Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: '"Roboto", sans-serif' }}>
                              {new Date(enrollment.enrollment_date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Completion Reward */}
                        <Paper sx={{ p: 2, backgroundColor: "rgba(163, 110, 41, 0.1)", mb: 3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                            Completion Reward
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#A36E29", fontFamily: '"Roboto", sans-serif' }}>
                            {enrollment.completion_reward}
                          </Typography>
                        </Paper>
                      </Box>

                      {/* Actions */}
                      <Box sx={{ 
                        display: "flex", 
                        gap: 2, 
                        mt: "auto",
                        pt: 2
                      }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => handleViewDetails(enrollment.id)}
                          sx={{
                            borderColor: "#A36E29",
                            color: "#A36E29",
                            fontFamily: '"Roboto", sans-serif',
                            "&:hover": {
                              borderColor: "#A36E29",
                              backgroundColor: "rgba(163, 110, 41, 0.04)"
                            }
                          }}
                        >
                          View Details
                        </Button>
                        {enrollment.status === 'active' && (
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleMakePayment(enrollment.id)}
                            sx={{
                              background: "linear-gradient(to right, #d4a76a, #A36E29)",
                              fontFamily: '"Roboto", sans-serif',
                              "&:hover": {
                                background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                              }
                            }}
                          >
                            Make Payment
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Enroll New Button */}
          {enrollments && enrollments.length > 0 && (
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              {/* <Button
                variant="contained"
                onClick={handleEnrollNew}
                sx={{
                  background: "linear-gradient(to right, #d4a76a, #A36E29)",
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontFamily: '"Roboto", sans-serif',
                  "&:hover": {
                    background: "linear-gradient(to right, #c18a3d, #8b5a1f)"
                  }
                }}
              >
                Enroll in New Scheme
              </Button> */}
            </Box>
          )}
        </Container>
      </Box>
    </Container>
  );
};

export default SchemesV2MySchemes;
